import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { from, map, Observable, switchMap } from 'rxjs';

import { environment } from '../../../../environments/environment';

interface TranscriptionResponse {
  text: string;
}

/** The backend expects WAV, PCM 16-bit signed, 16 kHz, mono. */
const TARGET_SAMPLE_RATE = 16000;

/**
 * Microphone recording + speech-to-text for the AI consultation composer.
 *
 * Records via {@link MediaRecorder} (whatever compressed format the browser supports — webm/opus on
 * Chromium/Firefox, mp4 on Safari), then converts the take client-side to the exact format the
 * backend transcriber expects (WAV PCM16 / 16 kHz / mono) using `decodeAudioData` +
 * `OfflineAudioContext` resampling and a hand-rolled RIFF encoder — no extra dependency.
 *
 * The upload goes through {@link HttpClient}, so the `AuthInterceptor` attaches the Bearer token.
 */
@Injectable({ providedIn: 'root' })
export class SpeechToTextService {
  private readonly url = `${environment.apiEndpoint}/ai/chat/transcribe`;

  private recorder?: MediaRecorder;
  private stream?: MediaStream;
  private chunks: Blob[] = [];
  private levelContext?: AudioContext;
  private analyser?: AnalyserNode;
  private analyserBuffer?: Float32Array;

  constructor(private readonly http: HttpClient) {}

  /** Whether this browser can record audio at all (drives showing/hiding the mic button). */
  public static isSupported(): boolean {
    return typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  }

  /**
   * Asks for microphone access and starts recording. Rejects with the `getUserMedia` `DOMException`
   * (e.g. `NotAllowedError`) so the caller can phrase a precise hint.
   */
  public async startRecording(): Promise<void> {
    if (this.recorder) {
      this.cancelRecording(); // defensive — never run two takes at once
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.stream = stream;
    this.chunks = [];
    const mimeType = this.pickMimeType();
    this.recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    this.recorder.ondataavailable = (event: BlobEvent): void => {
      if (event.data.size > 0) {
        this.chunks.push(event.data);
      }
    };
    this.recorder.start();
    this.startLevelAnalysis(stream);
  }

  /**
   * Current microphone input level, 0..1 — perceptually scaled so normal speech spans most of the
   * range. Returns 0 while not recording. Drives the live waveform in the composer.
   */
  public getInputLevel(): number {
    if (!this.analyser || !this.analyserBuffer) {
      return 0;
    }
    this.analyser.getFloatTimeDomainData(this.analyserBuffer as Float32Array<ArrayBuffer>);
    let sum = 0;
    for (let i = 0; i < this.analyserBuffer.length; i++) {
      sum += this.analyserBuffer[i] * this.analyserBuffer[i];
    }
    const rms = Math.sqrt(sum / this.analyserBuffer.length);
    return Math.min(1, Math.pow(rms * 5, 0.7));
  }

  /** Stops the take, releases the microphone (browser indicator goes off) and returns the audio. */
  public stopRecording(): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      const recorder = this.recorder;
      if (!recorder || recorder.state === 'inactive') {
        this.cleanup();
        reject(new Error('No active recording'));
        return;
      }
      const type = recorder.mimeType || 'audio/webm';
      recorder.onstop = (): void => {
        const blob = new Blob(this.chunks, { type });
        this.cleanup();
        resolve(blob);
      };
      recorder.onerror = (): void => {
        this.cleanup();
        reject(new Error('Recording failed'));
      };
      recorder.stop();
    });
  }

  /** Discards the current take and releases the microphone. Safe to call in any state. */
  public cancelRecording(): void {
    const recorder = this.recorder;
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null;
      recorder.ondataavailable = null;
      try {
        recorder.stop();
      } catch {
        // already stopped — nothing to do
      }
    }
    this.cleanup();
  }

  /** Converts the recorded audio to the backend's WAV format and posts it for transcription. */
  public transcribe(recording: Blob): Observable<string> {
    return from(this.toWav(recording)).pipe(
      switchMap((wav: Blob) => {
        const formData = new FormData();
        formData.append('audio', wav, 'recording.wav');
        return this.http.post<TranscriptionResponse>(this.url, formData);
      }),
      map((response: TranscriptionResponse): string => response?.text ?? ''),
    );
  }

  // ── audio conversion ────────────────────────────────────────────────────

  /** Decodes the compressed take, resamples it to 16 kHz mono and encodes WAV PCM 16-bit. */
  private async toWav(recording: Blob): Promise<Blob> {
    const encoded = await recording.arrayBuffer();
    const decodeContext = new AudioContext();
    let decoded: AudioBuffer;
    try {
      decoded = await decodeContext.decodeAudioData(encoded);
    } finally {
      await decodeContext.close();
    }

    // A 1-channel OfflineAudioContext destination downmixes to mono and resamples in one pass.
    const length = Math.max(1, Math.ceil(decoded.duration * TARGET_SAMPLE_RATE));
    const offline = new OfflineAudioContext(1, length, TARGET_SAMPLE_RATE);
    const source = offline.createBufferSource();
    source.buffer = decoded;
    source.connect(offline.destination);
    source.start(0);
    const rendered = await offline.startRendering();

    return this.encodeWavPcm16(rendered.getChannelData(0), TARGET_SAMPLE_RATE);
  }

  /** Writes a canonical 44-byte RIFF/WAVE header followed by little-endian PCM 16-bit samples. */
  private encodeWavPcm16(samples: Float32Array, sampleRate: number): Blob {
    const bytesPerSample = 2; // 16-bit mono
    const dataSize = samples.length * bytesPerSample;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);
    const writeAscii = (offset: number, value: string): void => {
      for (let i = 0; i < value.length; i++) {
        view.setUint8(offset + i, value.charCodeAt(i));
      }
    };

    writeAscii(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeAscii(8, 'WAVE');
    writeAscii(12, 'fmt ');
    view.setUint32(16, 16, true); // fmt chunk size
    view.setUint16(20, 1, true); // audio format: PCM
    view.setUint16(22, 1, true); // channels: mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * bytesPerSample, true); // byte rate
    view.setUint16(32, bytesPerSample, true); // block align
    view.setUint16(34, 16, true); // bits per sample
    writeAscii(36, 'data');
    view.setUint32(40, dataSize, true);

    let offset = 44;
    for (let i = 0; i < samples.length; i++, offset += bytesPerSample) {
      const clamped = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
    }

    return new Blob([buffer], { type: 'audio/wav' });
  }

  /** First recording format this browser supports (Chromium/Firefox → webm/opus, Safari → mp4). */
  private pickMimeType(): string | undefined {
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
    return candidates.find((type: string) => MediaRecorder.isTypeSupported(type));
  }

  /** Taps the live stream with an AnalyserNode (no audible output) so the UI can draw a waveform. */
  private startLevelAnalysis(stream: MediaStream): void {
    try {
      this.levelContext = new AudioContext();
      const analyser = this.levelContext.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.6;
      this.levelContext.createMediaStreamSource(stream).connect(analyser);
      this.analyser = analyser;
      this.analyserBuffer = new Float32Array(analyser.fftSize);
    } catch {
      this.analyser = undefined; // visualization is optional — recording works without it
    }
  }

  private cleanup(): void {
    this.stream?.getTracks().forEach((track: MediaStreamTrack) => track.stop());
    this.stream = undefined;
    this.recorder = undefined;
    this.chunks = [];
    this.analyser = undefined;
    this.analyserBuffer = undefined;
    void this.levelContext?.close().catch(() => undefined);
    this.levelContext = undefined;
  }
}
